import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import {
  useGetBuildingVehicleTypesQuery,
  useGetVehicleTypeOverridesQuery,
} from '@/api/adminServiceApi'
import WidgetPaper from '@/components/WidgetPaper'
import SearchInput from '@/components/SearchInput'
import TableEmptyPlaceholder from '@/components/TableEmptyPlaceholder'
import TableRowsSkeleton from '@/components/TableRowsSkeleton'
import { getVehicleTypeChipProps } from '@/utils/vehicle'
import AddButton from '../access/AddButton'
import OverrideDialog from './override-dialog'
import DeleteOverrideDialog from './delete-dialog'

const VehicleTypeOverrides = ({ ref }) => {
  const { t } = useTranslation()
  const buildingId = useSelector(selectActiveBuildingId)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [dialogState, setDialogState] = useState<{ mode: 'create' | 'edit', item?: any } | null>(null)
  const [itemToDelete, setItemToDelete] = useState<any>(null)

  const {
    data: vehicleTypesResponse,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetBuildingVehicleTypesQuery(buildingId, { skip: !buildingId })

  const vehicleTypeOptions = useMemo(() => (
    (vehicleTypesResponse?.items || []).map((it) => ({
      value: it.code,
      label: it.label || it.code,
    }))
  ), [vehicleTypesResponse])

  const isSupported = vehicleTypeOptions.length > 1

  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  } = useGetVehicleTypeOverridesQuery(
    { buildingId, search, page: page + 1, size: rowsPerPage },
    { skip: !buildingId },
  )

  const items = data?.items || []
  const hasRows = items.length > 0

  const handleSearch = (value) => {
    setSearch(value.trim())
    setPage(0)
  }

  return (
    <WidgetPaper ref={ref} title={t('vehicle_type_overrides_title')}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('vehicle_type_overrides_explained')}
      </Typography>

      {!isTypesLoading && !isTypesError && !isSupported && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('vehicle_type_overrides_not_supported')}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flex: 1, minWidth: 0, mb: 2 }}
      >
        <SearchInput
          placeholder={t('enter_number')}
          onChange={handleSearch}
          delay={300}
          disabled={!hasRows && !search}
        />
        {isSupported && (
          <>
            <Divider orientation="vertical" flexItem />
            <AddButton onClick={() => setDialogState({ mode: 'create' })} />
          </>
        )}
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('plate_number')}</TableCell>
              <TableCell>{t('vehicle_type')}</TableCell>
              <TableCell>{t('comment')}</TableCell>
              <TableCell>{t('vehicle_type_overrides_updated')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRowsSkeleton rows={3} columns={5} />
            ) : items.map((item) => {
              const chip = getVehicleTypeChipProps(item.vehicle_type, t)
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" noWrap>{item.plate_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={chip.color}
                      label={item.vehicle_type_label || chip.label}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={item.comment || ''}>
                      {item.comment || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.updated_at ? dayjs(item.updated_at).format('DD.MM.YYYY HH:mm') : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.updated_by_username || item.created_by_username || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {isSupported && (
                      <Tooltip title={t('edit')}>
                        <IconButton
                          size="small"
                          onClick={() => setDialogState({ mode: 'edit', item })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setItemToDelete(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {isSuccess && !hasRows && !isFetching && (
        <TableEmptyPlaceholder
          icon={LocalShippingIcon}
          message={search ? t('list_empty') : t('vehicle_type_overrides_empty')}
        />
      )}

      {isSuccess && (data?.total || 0) > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={data?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
        />
      )}

      {isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <AlertTitle>{t('alert')}</AlertTitle>
          {t('error_occurred')}
        </Alert>
      )}

      {dialogState && (
        <OverrideDialog
          open
          mode={dialogState.mode}
          item={dialogState.item}
          buildingId={buildingId}
          vehicleTypeOptions={vehicleTypeOptions}
          onClose={() => setDialogState(null)}
        />
      )}

      {itemToDelete && (
        <DeleteOverrideDialog
          open
          item={itemToDelete}
          buildingId={buildingId}
          onClose={() => setItemToDelete(null)}
        />
      )}
    </WidgetPaper>
  )
}

export default VehicleTypeOverrides
